import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Search, MapPin, ChevronDown, ChevronRight, User, ShoppingCart, Heart, Package, Menu, LayoutGrid } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { Icon } from "./icons";

export function StoreHeader() {
  const { count } = useCart();
  const { currency, setCurrency } = useCurrency();
  const { user } = useAuth();
  const nav = useNavigate();
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [suggest, setSuggest] = useState(null);
  const [showMega, setShowMega] = useState(false);
  const [megaCat, setMegaCat] = useState(null);
  const boxRef = useRef();

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);

  useEffect(() => {
    if (!q.trim()) { setSuggest(null); return; }
    const t = setTimeout(() => api.get(`/search/suggest?q=${encodeURIComponent(q)}`).then((r) => setSuggest(r.data)).catch(() => {}), 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setSuggest(null); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const search = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (cat !== "all") params.set("category", cat);
    setSuggest(null);
    nav(`/shop?${params.toString()}`);
  };

  return (
    <header className="sticky top-0 z-50">
      {/* top strip */}
      <div className="bg-[#0A1026] text-zinc-300 text-xs">
        <div className="max-w-[1500px] mx-auto px-4 flex items-center justify-between h-8">
          <span className="flex items-center gap-1.5"><MapPin size={13} className="text-[#FF6A1A]" />Deliver to <b className="text-white">Nigeria</b></span>
          <div className="flex items-center gap-4">
            <Link to="/track" className="hover:text-white">Track Order</Link>
            <div className="flex items-center rounded overflow-hidden border border-zinc-700">
              {["USD", "NGN"].map((c) => (
                <button key={c} onClick={() => setCurrency(c)} className={`px-2 py-0.5 ${currency === c ? "bg-[#FF6A1A] text-white" : ""}`} data-testid={`currency-${c}`}>{c}</button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* main header */}
      <div className="bg-[#101A3D] text-white">
        <div className="max-w-[1500px] mx-auto px-4 flex items-center gap-4 h-16">
          <Link to="/" className="shrink-0" data-testid="logo-link">
            <img src="/qava-logo.png" alt="Qava" className="h-10 w-auto object-contain" />
          </Link>

          <form onSubmit={search} ref={boxRef} className="flex-1 relative max-w-3xl">
            <div className="flex bg-white rounded-md overflow-hidden h-11 shadow-sm">
              <div className="relative hidden sm:block">
                <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-full bg-zinc-100 text-zinc-700 text-sm px-3 pr-7 border-r border-zinc-200 outline-none appearance-none cursor-pointer max-w-[130px]" data-testid="search-category">
                  <option value="all">All</option>
                  {cats.map((c) => <option key={c.key} value={c.query_category || c.key}>{c.name}</option>)}
                </select>
                <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              </div>
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search for products, brands and categories"
                className="flex-1 px-3 text-zinc-800 text-sm outline-none" data-testid="search-input" />
              <button type="submit" className="bg-[#FF6A1A] hover:bg-[#e85f12] px-5 flex items-center" data-testid="search-btn"><Search size={20} /></button>
            </div>

            {suggest && (suggest.products.length > 0 || suggest.categories.length > 0) && (
              <div className="absolute top-12 left-0 right-0 bg-white text-zinc-800 rounded-md shadow-xl border border-zinc-200 overflow-hidden z-50" data-testid="search-suggestions">
                {suggest.categories.map((c) => (
                  <Link key={c.key} to={`/category/${c.key}`} onClick={() => setSuggest(null)} className="flex items-center gap-2 px-4 py-2 hover:bg-zinc-50 text-sm">
                    <LayoutGrid size={14} className="text-zinc-400" /> in <b>{c.name}</b>
                  </Link>
                ))}
                {suggest.products.map((p) => (
                  <Link key={p.slug} to={`/product/${p.slug}`} onClick={() => setSuggest(null)} className="flex items-center gap-3 px-4 py-2 hover:bg-zinc-50">
                    <img src={(p.images || [])[0]} alt="" className="w-9 h-9 object-cover rounded" />
                    <span className="text-sm line-clamp-1 flex-1">{p.title}</span>
                  </Link>
                ))}
              </div>
            )}
          </form>

          <div className="flex items-center gap-1 sm:gap-3 shrink-0">
            <Link to={user ? "/account" : "/login"} className="hidden sm:flex flex-col leading-tight px-2 py-1 rounded hover:bg-white/10" data-testid="account-link">
              <span className="text-[11px] text-zinc-300">{user ? "Hi, " + (user.name?.split(" ")[0] || "") : "Sign In"}</span>
              <span className="text-sm font-semibold flex items-center gap-1">Account <ChevronDown size={12} /></span>
            </Link>
            <Link to="/account" className="hidden md:flex flex-col leading-tight px-2 py-1 rounded hover:bg-white/10">
              <span className="text-[11px] text-zinc-300">Returns</span><span className="text-sm font-semibold">& Orders</span>
            </Link>
            <Link to="/account?tab=wishlist" className="p-2 rounded hover:bg-white/10 relative" data-testid="wishlist-link"><Heart size={22} /></Link>
            <Link to="/cart" className="p-2 rounded hover:bg-white/10 relative flex items-center gap-1" data-testid="cart-link">
              <div className="relative"><ShoppingCart size={24} />
                {count > 0 && <span className="absolute -top-1.5 -right-1.5 bg-[#FF6A1A] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center" data-testid="cart-count">{count}</span>}
              </div>
              <span className="hidden lg:block text-sm font-semibold">Cart</span>
            </Link>
          </div>
        </div>
      </div>

      {/* secondary nav — category buttons */}
      <div className="bg-[#17224A] text-zinc-200 text-sm relative">
        <div className="max-w-[1500px] mx-auto px-4 flex items-center gap-1.5 h-12 overflow-x-auto scrollbar-hide">
          <button onMouseEnter={() => setShowMega(true)} onMouseLeave={() => setShowMega(false)}
            className="flex items-center gap-1.5 font-semibold bg-[#FF6A1A] hover:bg-[#e85f12] text-white px-3.5 h-8 rounded-full whitespace-nowrap transition-colors shrink-0" data-testid="all-categories-btn">
            <Menu size={15} />All Categories
          </button>
          {cats.map((c) => (
            <Link key={c.key} to={`/category/${c.key}`}
              className="flex items-center gap-1.5 px-3 h-8 rounded-full whitespace-nowrap bg-white/5 hover:bg-white/15 hover:text-white transition-colors shrink-0 border border-white/5"
              data-testid={`nav-cat-${c.key}`}>
              <Icon name={c.icon} size={14} className="text-[#FF8A4A]" />{c.name}
            </Link>
          ))}
        </div>

        {showMega && (
          <div onMouseEnter={() => setShowMega(true)} onMouseLeave={() => { setShowMega(false); setMegaCat(null); }}
            className="absolute top-12 left-0 flex bg-white text-zinc-800 shadow-2xl border border-zinc-200 z-50 w-[780px] max-w-[95vw]">
            <div className="w-64 shrink-0 py-2 border-r border-zinc-100 max-h-[460px] overflow-y-auto">
              {cats.map((c) => (
                <Link key={c.key} to={`/category/${c.key}`} onMouseEnter={() => setMegaCat(c)}
                  className={`flex items-center justify-between gap-3 px-4 py-2 text-sm ${megaCat?.key === c.key ? "bg-[#FFF3E6] text-[#FF6A1A]" : "hover:bg-zinc-50"}`} data-testid={`mega-${c.key}`}>
                  <span className="flex items-center gap-3"><Icon name={c.icon} size={16} className="text-[#FF6A1A]" />{c.name}</span>
                  <ChevronRight size={13} className="text-zinc-300" />
                </Link>
              ))}
            </div>
            <div className="flex-1 p-5">
              {(megaCat || cats[0]) && (
                <>
                  <div className="flex items-center gap-3 mb-4">
                    <img src={(megaCat || cats[0]).image} alt="" className="w-12 h-12 rounded-md object-cover" />
                    <div>
                      <Link to={`/category/${(megaCat || cats[0]).key}`} className="font-semibold text-sm hover:text-[#FF6A1A]">{(megaCat || cats[0]).name}</Link>
                      <p className="text-xs text-zinc-400">Shop all {(megaCat || cats[0]).name}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                    {((megaCat || cats[0]).subcategories || []).map((s) => (
                      <Link key={s} to={`/shop?q=${encodeURIComponent(s)}`} className="text-[13px] text-zinc-600 hover:text-[#FF6A1A] py-1" data-testid={`sub-${s}`}>{s}</Link>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
